import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useBlocker, useLocation } from 'react-router-dom';

/* CSS */
import styles from './NewTripPage.module.css';
import shared from '../styles/shared.module.css';

/* Floating UI for date picker */
import 'react-datepicker/dist/react-datepicker.css';

/* Icons */
import trashcan_icon from '../assets/icons/trashcan_icon.svg';
import tick_icon from '../assets/icons/tick_icon.svg';

import share_clicked from '../assets/icons/share_sticky_clicked.svg';
import share_unclicked from '../assets/icons/share_sticky_unclicked.svg';
import cost_breakdown_clicked from '../assets/icons/cost_clicked.svg';
import cost_breakdown_unclicked from '../assets/icons/cost_unclicked.svg';

import loading_icon from '../assets/icons/loading.svg';
import cloud_sync from '../assets/icons/cloud_sync.svg';
import cloud_cross from '../assets/icons/cloud_cross.svg';

/* Modals */
import ShareModal from '../components/share/ShareModal';
import ConfirmationModal from '../components/cards/ConfirmationModal';
import PostSavePanel from '../components/itinerary/PostSavePanel';

/* Utils */
import { tripUtils } from '../utils/tripUtils';
import { formatDateOnly, parseDateOnly } from '../utils/dateUtils';

/* Hooks */
import { usePresence } from '../hooks/usePresence';
import { useSaveStatus } from '../hooks/useSaveStatus';

/* Components */
import { PresenceAvatars } from '../components/itinerary/PresenceAvatars';

/* TripContext */
import { TripProvider, useTripContext } from '../components/itinerary/TripContext';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

/* TripHeader */
import TripHeader from '../components/itinerary/TripHeader';

/* ItineraryBoard */
import ItineraryBoard from '../components/itinerary/ItineraryBoard';

export const NewTripContent = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const { id: tripId } = useParams();
    const {
        title, setTitle,
        selectedCountries, setSelectedCountries,
        fromDateObj, setFromDateObj,
        toDateObj, setToDateObj,
        durationInDays, setDurationInDays,
        itineraryDays, setItineraryDays,
        defaultColumns, setDefaultColumns,
        setTripMembers,
        setStructureVersion,
        canEdit, setCanEdit,
        reloadRef,
        isDirty, setIsDirty
    } = useTripContext();
    const { currentUser } = useAuthContext();
    const { showToast } = useToast();

    // Presence avatars
    const { roster, editingIds } = usePresence(tripId);

    // Save-status indicator (saving / autosaved / failed)
    const saveStatus = useSaveStatus();

    // Trip title edit
    const [titleError, setTitleError] = useState(false)
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    const titleInputRef = useRef(null);
    const titleSectionRef = useRef(null);

    // Country selection
    const [missingDestination, setMissingDestination] = useState(false);

    const destinationWrapperRef = useRef(null);
    const destinationInputRef = useRef(null);

    // Raw digit buffers for date masks
    const [missingFromDate, setMissingFromDate] = useState(false);
    const [missingToDate, setMissingToDate] = useState(false);

    // Modal states
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    // Save state
    const [savedTripId, setSavedTripId] = useState(null);
    const [currentRole, setCurrentRole] = useState('owner');

    // Icon states
    const [hoverShareBtn, setHoverShareBtn] = useState(false);
    const [hoverCostBreakdownBtn, setHoverCostBreakdownBtn] = useState(false);

    // Sticky menu: true once the share bar pins to the top of the viewport
    const [isBarStuck, setIsBarStuck] = useState(false);
    const stickySentinelRef = useRef(null);

    // Watch the zero-height sentinel sitting just above the share bar
    useEffect(() => {
        const sentinel = stickySentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsBarStuck(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [tripId]);

    // Date input refs for caret control
    const fromDateInputRef = useRef(null);
    const toDateInputRef = useRef(null);

    // A live snapshot of everything the whole-trip save needs (so the debounced save isn't stale)
    const tripSnapshotRef = useRef();
    useEffect(() => {
        tripSnapshotRef.current = { title, selectedCountries, fromDateObj, toDateObj, defaultColumns, itineraryDays };
    });

    // Date-range change (edit mode) → debounced whole-trip re-sync
    const resyncTimer = useRef(null);
    const resyncInit = useRef(false);

    useEffect(() => {
        if (!tripId || !fromDateObj || !toDateObj || !canEdit) return;
        if (!resyncInit.current) { resyncInit.current = true; return; }   // skip the initial load

        clearTimeout(resyncTimer.current);
        resyncTimer.current = setTimeout(() => {
            const s = tripSnapshotRef.current;
            tripUtils.saveTrip({
                id: tripId,
                title: s.title.trim(),
                location: s.selectedCountries,
                fromDate: s.fromDateObj,
                toDate: s.toDateObj,
                layout: s.defaultColumns,
                days: s.itineraryDays.map(day => ({
                    id: day.id, date: formatDateOnly(day.date),
                    rows: day.rows, columns: day.columns, note: day.note || ''
                }))
            }).catch(() => showToast('Failed to save date change', 'error'));
        }, 800);
    }, [fromDateObj, toDateObj, tripId, canEdit]);

    /* -------------- */
    /* --- HELPER --- */
    /* -------------- */

    // Helper to decide which page to go back to (or default to My Trips page)
    const getReturnPath = () => {
        return location.state?.from || '/trips';
    }

    /* -------------- */
    /* --- ACTION --- */
    /* -------------- */

    const handleSaveClick = async (onSuccessCallback) => {
        let hasError = false;

        // Validate title
        const isTitleMissing = !title.trim();
        if (isTitleMissing) {
            setTitleError(true);

            // Scroll to title section
            requestAnimationFrame(() => {
                if (titleSectionRef.current) {
                    titleSectionRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            });
            setIsEditingTitle(true);
            hasError = true;
        } else {
            setTitleError(false);
        }

        // Validate destination
        const isDestinationMissing = selectedCountries.length === 0;
        setMissingDestination(isDestinationMissing);

        if (isDestinationMissing) {
            if (!hasError && destinationInputRef.current) {
                destinationInputRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            hasError = true;
        }

        // Validate dates
        const isFromMissing = !fromDateObj;
        const isToMissing = !toDateObj;

        setMissingFromDate(isFromMissing);
        setMissingToDate(isToMissing);

        if (isFromMissing || isToMissing || durationInDays === null || durationInDays <= 0) {
            // Scroll to date section (but keep title error visible too)
            if (!hasError && fromDateInputRef.current) {
                fromDateInputRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            hasError = true;
        }

        if (hasError) return;

        const newTripId = tripId || crypto.randomUUID();

        const newTrip = {
            id: newTripId,
            title: title.trim(),
            location: selectedCountries,
            fromDate: fromDateObj,
            toDate: toDateObj,
            layout: defaultColumns,
            days: itineraryDays.map(day => ({
                id: day.id,
                date: formatDateOnly(day.date),
                rows: day.rows,
                columns: day.columns,
                note: day.note || ''
            }))
        }

        await tripUtils.saveTrip(newTrip);

        setIsDirty(false);
        setSavedTripId(newTripId);

        if (onSuccessCallback) {
            onSuccessCallback();
        } else {
            setShowSuccessModal(true);
        }

        return true;
    };

    const handleSuccessDone = () => {
        setShowSuccessModal(false);
        navigate('/trips');
    };

    const handleSuccessStay = () => {
        setShowSuccessModal(false);

        if (!tripId && savedTripId) {
            navigate(`/trip/${savedTripId}`, { replace: true });
        }
    };

    const handleDiscardClick = () => {
        // Edit mode: Discard permanently deletes the saved trip (always confirm)
        if (tripId) {
            setShowDeleteModal(true);
            return;
        }
        // Create mode: just abandon the unsaved draft
        if (!isDirty) {
            navigate(getReturnPath());
            return;
        }
        setShowDiscardModal(true);
    };

    const handleConfirmDiscard = () => {
        setIsDirty(false);
        setShowDiscardModal(false);
        navigate(getReturnPath());
    };

    const handleConfirmDelete = async () => {
        try {
            await tripUtils.deleteTrip(tripId);
            setIsDirty(false); // Clear before navigating so the unsaved-changes blocker stays quiet
            setShowDeleteModal(false);
            showToast('Trip deleted', 'success');
            navigate(getReturnPath());
        } catch (error) {
            console.error('Failed to delete trip', error);
            setShowDeleteModal(false);
            showToast('Could not delete trip. Please try again.', 'error');
        }
    };

    const cancelNavigation = () => {
        if (blocker.state === 'blocked') {
            blocker.reset(); // Close modal and stay on page
        }
    };

    const confirmNavigation = () => {
        if (blocker.state === 'blocked') {
            blocker.proceed(); // Proceed to the new page (discarding changes)
        }
    };

    // Watch for navigation attempts (isDirty is true = blocks attempt)
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            !tripId && isDirty && currentLocation.pathname !== nextLocation.pathname
    );

    // Watch for browser reload/close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome/modern browsers
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // Day Generation
    useEffect(() => {
        if (!fromDateObj || durationInDays == null || durationInDays <= 0) {
            if (!tripId) setItineraryDays([]);
            return;
        }

        // Access the current data without adding it to the dependency array (which avoids loops)
        setItineraryDays(prevDays => {

            // If we already have the correct number of days loaded (from the other useEffect), don't regenerate
            if (prevDays.length === durationInDays) {
                const firstDayDate = prevDays[0] ? new Date(prevDays[0].date) : null;
                if (firstDayDate && firstDayDate.toDateString() === fromDateObj.toDateString()) {
                    return prevDays;
                }
            }

            const days = [];
            for (let i = 0; i < durationInDays; i++) {

                // Calculate the date for this day
                const date = new Date(fromDateObj);
                date.setDate(date.getDate() + i);

                if (i < prevDays.length) {
                    days.push({
                        ...prevDays[i],
                        date: date
                    })
                } else {
                    days.push({
                        id: crypto.randomUUID(),
                        date: date,
                        rows: [tripUtils.createEmptyRow(defaultColumns)],
                        columns: defaultColumns.map(col => ({ ...col })),
                        note: ''
                    });
                }
            }
            return days;
        });

    }, [fromDateObj, durationInDays, tripId]);

    // Load trip data if editing an existing trip
    useEffect(() => {
        if (!tripId) { setCanEdit(true); return; }   // create mode → editable

        const loadTrip = async () => {

            // Retrieve all the trips
            const foundTrip = await tripUtils.getTrip(tripId);

            if (!foundTrip) {
                alert("Trip not found!");
                navigate('/trips');
                return;
            }

            // Populate trip title + meta
            setTitle(foundTrip.title);
            setSelectedCountries(foundTrip.locations || foundTrip.location || []);

            // Seed the client's structure version (sent on item writes so the server can detect stale-layout edits)
            if (foundTrip.structure_version != null) setStructureVersion(foundTrip.structure_version);

            // Owner/editor can edit; viewer is read-only
            setCanEdit(['owner', 'editor'].includes(foundTrip.access?.role));
            setCurrentRole(foundTrip.access?.role || 'owner');

            if (foundTrip.fromDate) {
                const start = parseDateOnly(foundTrip.fromDate);
                setFromDateObj(start);
            }

            if (foundTrip.toDate) {
                const end = parseDateOnly(foundTrip.toDate);
                setToDateObj(end);
            }

            if (foundTrip.days && foundTrip.days.length > 0) {
                // The saved days are the source of truth — derive the count from them, not from date subtraction
                setDurationInDays(foundTrip.days.length);
            } else if (foundTrip.fromDate && foundTrip.toDate) {
                const duration = new Date(foundTrip.toDate) - new Date(foundTrip.fromDate);
                setDurationInDays(Math.floor(duration / (1000 * 60 * 60 * 24)) + 1);
            }

            // Migration: Legacy tags fallback
            const legacyTags = foundTrip.tags || ['Accomodation', 'Food & Drinks', 'Shopping', 'Transport', 'Leisure', 'Miscellaneous'];

            // Populate columns (and migrate tags)
            let loadedLayout = foundTrip.layout;
            if (loadedLayout) {
                loadedLayout = loadedLayout.map(col => {
                    if (col.type === 'tags' && !col.tags) {
                        return { ...col, tags: legacyTags };
                    }
                    return col;
                });
                setDefaultColumns(loadedLayout);
            }

            // Populate itinerary days + rows
            if (foundTrip.days) {
                const mappedDays = foundTrip.days.map((day, index) => {

                    // Determine columns for this day
                    const rawColumns = day.columns || loadedLayout || defaultColumns;

                    // Apply migration to day columns as well
                    const dayColumns = rawColumns.map(col => {
                        // Is this a tags column BUT it's missing the list of tags?
                        if (col.type === 'tags' && !col.tags) {
                            return { ...col, tags: legacyTags };
                        }
                        return col;
                    });

                    return {
                        id: day.id || `${foundTrip.fromDate}-${index}`,
                        date: parseDateOnly(day.date),
                        rows: day.rows.length > 0 ? day.rows : [tripUtils.createEmptyRow(dayColumns)],
                        columns: dayColumns,
                        note: day.note || ''
                    };
                });
                setItineraryDays(mappedDays);
            }

            if (foundTrip.members?.length > 0) {
                setTripMembers(foundTrip.members.map(m => ({
                    id: m.user_id,
                    name: m.display_name || m.email,
                    avatar: m.profile_picture_url || null,
                    isMe: m.user_id === currentUser.id
                })));
            }
        }

        loadTrip();
    }, [tripId, navigate]);

    // Targeted refetch after a 409 conflict: refresh ONLY the itinerary + versions
    // Deliberately does NOT touch fromDate/toDate/title, so it can't retrigger the date-range re-sync or day-generation effects.
    const reloadItinerary = useCallback(async () => {
        if (!tripId) return;
        const t = await tripUtils.getTrip(tripId);
        if (!t) return;

        if (t.structure_version != null) setStructureVersion(t.structure_version);
        if (t.layout) setDefaultColumns(t.layout);

        if (t.days) {
            setItineraryDays(t.days.map((day, index) => {
                const cols = day.columns || t.layout || defaultColumns;
                return {
                    id: day.id || `${t.fromDate}-${index}`,
                    date: parseDateOnly(day.date),
                    rows: day.rows.length > 0 ? day.rows : [tripUtils.createEmptyRow(cols)],
                    columns: cols,
                    note: day.note || ''
                };
            }));
        }
    }, [tripId, defaultColumns, setStructureVersion, setDefaultColumns, setItineraryDays]);

    // Register the refetch so any consumer (e.g. ItineraryBoard) can call reloadTrip() after a conflict
    useEffect(() => {
        reloadRef.current = reloadItinerary;
    }, [reloadItinerary, reloadRef]);

    return (
        <main className={shared.mainContent}>
            {tripId && (
                <>
                    {/* Zero-height marker for the bar's resting position (see IntersectionObserver) */}
                    <div ref={stickySentinelRef} aria-hidden="true" />
                    <div className={`${styles.stickyRow} ${isBarStuck ? styles.stuck : ''}`}>
                        {/* Live status */}
                        <div className={styles.presenceGroup}>
                            <PresenceAvatars roster={roster} editingIds={editingIds} />

                            {saveStatus === 'saving' && (
                                <span className={styles.saveStatus}>
                                    <img src={loading_icon} alt="" className={`${styles.saveStatusIcon} ${styles.saveStatusSpin}`} />
                                    Saving changes...
                                </span>
                            )}
                            {saveStatus === 'saved' && (
                                <span className={styles.saveStatus}>
                                    <img src={cloud_sync} alt="" className={styles.saveStatusIcon} />
                                    Changes autosaved
                                </span>
                            )}
                            {saveStatus === 'error' && (
                                <span className={`${styles.saveStatus} ${styles.saveStatusError}`}>
                                    <img src={cloud_cross} alt="" className={styles.saveStatusIcon} />
                                    Failed to save changes:(
                                </span>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className={styles.stickyRowButtons}>
                            <button
                                onClick={() => { navigate(`/cost/${tripId}`) }}
                                onMouseEnter={() => setHoverCostBreakdownBtn(true)}
                                onMouseLeave={() => setHoverCostBreakdownBtn(false)}
                                className={`${styles.stickyRowButton} ${styles.costBreakdownBtn}`}
                                type='button'
                                data-tooltip='Cost breakdown'
                            >
                                <img src={hoverCostBreakdownBtn ? cost_breakdown_clicked : cost_breakdown_unclicked} alt="Cost breakdown" className={styles.costBreakdownIcon} />
                            </button>
                            <button
                                onClick={() => setShowShareModal(true)}
                                onMouseEnter={() => setHoverShareBtn(true)}
                                onMouseLeave={() => setHoverShareBtn(false)}
                                className={`${styles.stickyRowButton} ${styles.shareBtn}`}
                                type='button'
                                data-tooltip='Share trip'
                            >
                                <img src={hoverShareBtn ? share_clicked : share_unclicked} alt="Share trip" className={styles.shareIcon} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            <TripHeader
                isEditingTitle={isEditingTitle}
                setIsEditingTitle={setIsEditingTitle}
                titleError={titleError}
                setTitleError={setTitleError}
                missingDestination={missingDestination}
                setMissingDestination={setMissingDestination}
                missingFromDate={missingFromDate}
                setMissingFromDate={setMissingFromDate}
                missingToDate={missingToDate}
                setMissingToDate={setMissingToDate}
                titleInputRef={titleInputRef}
                titleSectionRef={titleSectionRef}
                destinationInputRef={destinationInputRef}
                fromDateInputRef={fromDateInputRef}
                toDateInputRef={toDateInputRef}
                destinationWrapperRef={destinationWrapperRef}
            />

            {/* Itinerary section */}
            {itineraryDays.length > 0 && (
                <ItineraryBoard />
            )}

            {/* Action buttons */}
            <section className={styles.actionButtons}>

                {/* Discard */}
                {canEdit && (
                    <button
                        className={styles.discardButton}
                        onClick={handleDiscardClick}
                        type='button'
                    >
                        <img
                            src={trashcan_icon}
                            alt="Discard"
                            className={styles.discardIcon}
                        />
                        <h1 className={styles.discardText}>{tripId ? "Delete Trip" : "Discard"}</h1>
                    </button>
                )}

                {/* Save */}
                {!tripId && (
                    <button className={styles.saveButton} onClick={() => handleSaveClick()} type='button'>
                        <h1 className={styles.saveText}>Save</h1>
                        <img
                            src={tick_icon}
                            alt="Save"
                            className={styles.saveIcon}
                        />
                    </button>
                )}
            </section>

            {/* --- MODAL 1: UNSAVED CHANGES --- */}
            <ConfirmationModal
                isOpen={blocker.state === 'blocked' && !tripId}
                title="Unsaved Changes"
                message="Are you sure you want to leave without saving?"
                onClose={cancelNavigation}
                onConfirm={confirmNavigation}
                confirmText="Leave"
                cancelText="Stay"
                isDanger={true}
            />

            {/* --- MODAL 2: SUCCESS --- */}
            <PostSavePanel
                isOpen={showSuccessModal}
                tripTitle={title}
                tripId={savedTripId}
                access={{ isMember: true, role: currentRole }}
                onClose={handleSuccessStay}
                onDone={handleSuccessDone}
            />

            {/* --- MODAL 3: DISCARD --- */}
            <ConfirmationModal
                isOpen={showDiscardModal}
                title="Discard Changes?"
                message="Are you sure you want to discard your changes? This action cannot be undone."
                onClose={() => setShowDiscardModal(false)}
                onConfirm={handleConfirmDiscard}
                confirmText="Discard"
                cancelText="Cancel"
                isDanger={true}
                showCancel={true}
            />

            {/* --- MODAL 4: SHARE --- */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                tripId={tripId}
                tripTitle={title}
                access={{ isMember: true, role: currentRole }}
            />

            {/* --- MODAL 5: DELETE TRIP --- */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Delete Trip?"
                message="This will permanently delete this trip and its entire itinerary. This action cannot be undone."
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                confirmText="Delete"
                cancelText="Cancel"
                isDanger={true}
                showCancel={true}
            />
        </main >
    );
};

const NewTripPage = () => {
    return (
        <TripProvider>
            <NewTripContent />
        </TripProvider>
    );
};

export default NewTripPage;
